import { useState } from "react";
import { PenLine, Upload } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";
import SignatureUpload from "@/components/SignatureUpload";
import {
  AGREEMENT_ESIGN_DISCLOSURE,
  isValidPrintedName,
  isValidSignatureDataUrl,
  type SignatureSource,
} from "@shared/agreement";

// Re-exported so the pages hosting this block import the validators from the
// same place as the component rather than reaching into shared/ separately.
export { isValidPrintedName, isValidSignatureDataUrl };

/**
 * Signature block for the Master Agent Participation Agreement.
 *
 * Mirrors the acknowledgment block on page 13 of the approved document, which
 * has three fields: Signature, Agent Name, and Date. So the associate draws a
 * signature AND types their full legal name; the date is applied by the server
 * on completion and is shown here read-only.
 *
 * This REPLACES the old "I Agree" checkbox entirely, per the client's
 * instruction of 15 September 2026. There is no tick box any more: drawing a
 * signature is a more deliberate act of assent than ticking a box, and
 * Section 26 of the agreement expressly provides for it.
 *
 * `theme` exists because the two hosts have opposite palettes: /join-agent is
 * a dark marketing page and the dashboard gate sits on the dark app shell.
 */
export interface AgreementSignatureProps {
  printedName: string;
  onPrintedNameChange: (value: string) => void;
  signatureDataUrl: string | null;
  /** Second argument records HOW the signature was produced, for the audit trail. */
  onSignatureChange: (dataUrl: string | null, source: SignatureSource) => void;
  theme?: "dark" | "light";
  /** Shown once the signer has attempted to submit. */
  nameError?: string | null;
  signatureError?: string | null;
  disabled?: boolean;
}

/** The date the agreement will carry, for display only. */
export function todayDisplay(): string {
  return new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

/** True when both required fields are present and usable. */
export function canSubmitSignature(printedName: string, signatureDataUrl: string | null): boolean {
  return isValidPrintedName(printedName) && isValidSignatureDataUrl(signatureDataUrl);
}

export default function AgreementSignature({
  printedName,
  onPrintedNameChange,
  signatureDataUrl,
  onSignatureChange,
  theme = "dark",
  nameError,
  signatureError,
  disabled,
}: AgreementSignatureProps) {
  const dark = theme === "dark";

  // Drawing is the default because it is the fastest path for most people.
  // Upload exists for anyone who cannot draw a usable signature with a mouse,
  // and for signers who already have a scanned signature they prefer to use.
  const [mode, setMode] = useState<SignatureSource>("drawn");

  const shell = dark ? "bg-[#84ab4a]/10 border-[#84ab4a]/20" : "bg-[#84ab4a]/5 border-[#84ab4a]/30";
  const labelCls = dark ? "text-white/60" : "text-gray-500";
  const valueCls = dark ? "text-white" : "text-gray-900";
  const helpCls = dark ? "text-white/40" : "text-gray-400";
  const inputCls = dark
    ? "bg-white/5 border-white/15 text-white placeholder:text-white/25 focus:border-[#84ab4a]"
    : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-300 focus:border-[#84ab4a]";

  return (
    <div className={`border rounded-xl p-4 space-y-5 ${shell}`}>
      <div className={`text-[11px] font-semibold uppercase tracking-wide ${labelCls}`}>
        Agent Acknowledgment
      </div>

      {/* 1. Signature: draw it, or upload an image of it. */}
      <div>
        <div className="flex items-center justify-between mb-1.5 gap-3 flex-wrap">
          <label className={`text-xs font-medium ${labelCls}`}>
            Signature <span className="text-[#84ab4a]">*</span>
          </label>
          <div
            role="tablist"
            aria-label="How to provide your signature"
            className={`inline-flex rounded-lg p-0.5 ${dark ? "bg-white/5" : "bg-gray-100"}`}
          >
            {([
              { key: "drawn" as const, label: "Draw", Icon: PenLine },
              { key: "uploaded" as const, label: "Upload", Icon: Upload },
            ]).map(({ key, label, Icon }) => {
              const active = mode === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  disabled={disabled}
                  // Switching clears the signature rather than carrying it
                  // across. Keeping a drawn signature while the Upload tab is
                  // showing an empty dropzone would misrepresent what is
                  // actually about to be signed.
                  onClick={() => { if (mode !== key) { setMode(key); onSignatureChange(null, key); } }}
                  className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md transition-colors disabled:opacity-40 ${
                    active
                      ? dark ? "bg-[#84ab4a] text-white" : "bg-white text-gray-900 shadow-sm"
                      : dark ? "text-white/50 hover:text-white/80" : "text-gray-500 hover:text-gray-800"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {mode === "drawn" ? (
          <SignaturePad
            theme={theme}
            disabled={disabled}
            onChange={dataUrl => onSignatureChange(dataUrl, "drawn")}
          />
        ) : (
          <SignatureUpload
            theme={theme}
            disabled={disabled}
            value={signatureDataUrl}
            onChange={dataUrl => onSignatureChange(dataUrl, "uploaded")}
          />
        )}
        {signatureError && <p className="text-xs text-red-400 mt-1.5">{signatureError}</p>}
      </div>

      {/* 2. Printed name */}
      <div>
        <label
          htmlFor="agreement-printed-name"
          className={`block text-xs font-medium mb-1.5 ${labelCls}`}
        >
          Agent Name <span className="text-[#84ab4a]">*</span>
        </label>
        <input
          id="agreement-printed-name"
          type="text"
          autoComplete="name"
          spellCheck={false}
          disabled={disabled}
          value={printedName}
          onChange={e => onPrintedNameChange(e.target.value)}
          placeholder="Print your full legal name"
          aria-invalid={!!nameError}
          aria-describedby={nameError ? "agreement-name-error" : "agreement-name-help"}
          className={`w-full rounded-lg border px-3 py-2.5 text-base outline-none transition-colors disabled:opacity-50 ${inputCls} ${
            nameError ? "border-red-400" : ""
          }`}
        />
        {nameError ? (
          <p id="agreement-name-error" className="text-xs text-red-400 mt-1.5">{nameError}</p>
        ) : (
          <p id="agreement-name-help" className={`text-[11px] mt-1.5 ${helpCls}`}>
            Type your name exactly as it should appear on the agreement.
          </p>
        )}
      </div>

      {/* 3. Date, server-applied. Read-only by design: a signing date taken
             from the browser clock is worth nothing on a contract. */}
      <div>
        <div className={`block text-xs font-medium mb-1.5 ${labelCls}`}>Date</div>
        <div
          className={`rounded-lg border px-3 py-2.5 text-base ${valueCls} ${
            dark ? "bg-white/[0.03] border-white/10" : "bg-gray-50 border-gray-200"
          }`}
        >
          {todayDisplay()}
        </div>
        <p className={`text-[11px] mt-1.5 ${helpCls}`}>
          Applied automatically when you complete the agreement.
        </p>
      </div>

      <p className={`text-[11px] leading-relaxed ${helpCls}`}>{AGREEMENT_ESIGN_DISCLOSURE}</p>
    </div>
  );
}
